import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id]
 * @property {string} [title]
 * @property {string} [name]
 * @property {boolean} [available]
 * @property {Object} [featured_media]
 * @property {Object} [featured_media.preview_image]
 * @property {string} [featured_media.preview_image.src]
 * @property {string} [featured_media.alt]
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {HTMLElement | null} */
  #footer = null;

  /** @type {number} */
  #showOffset = 220;

  /** @type {HTMLElement | null} */
  #originalParent = null;

  /** @type {Element | null} */
  #originalNextSibling = null;

  connectedCallback() {
    super.connectedCallback();

    this.#moveStickyBarToBody();
    this.#setupStickyBehavior();
    this.#bindStickyButtonClick();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    window.addEventListener('resize', this.#handleResize, { passive: true, signal });
    window.addEventListener('load', this.#handleResize, { passive: true, signal });

    this.#getInitialQuantity();

    requestAnimationFrame(() => {
      this.#updateStickyVisibility();
      this.#bindStickyButtonClick();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#restoreStickyBar();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  #moveStickyBarToBody() {
    const { stickyBar } = this.refs;
    if (!stickyBar || !stickyBar.parentElement) return;
    if (stickyBar.dataset.movedToBody === 'true') return;

    this.#originalParent = stickyBar.parentElement;
    this.#originalNextSibling = stickyBar.nextElementSibling;

    document.body.appendChild(stickyBar);
    stickyBar.dataset.movedToBody = 'true';
  }

  #restoreStickyBar() {
    const { stickyBar } = this.refs;
    if (!stickyBar || stickyBar.dataset.movedToBody !== 'true' || !this.#originalParent) return;

    if (this.#originalNextSibling && this.#originalNextSibling.parentNode === this.#originalParent) {
      this.#originalParent.insertBefore(stickyBar, this.#originalNextSibling);
    } else {
      this.#originalParent.appendChild(stickyBar);
    }

    stickyBar.dataset.movedToBody = 'false';
  }

  #setupStickyBehavior() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#buyButtonsBlock = productForm.closest('.buy-buttons-block');
    this.#footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    this.#updateStickyVisibility();
  }

  #bindStickyButtonClick() {
    const stickyButton = this.refs.addToCartButton;
    if (!stickyButton) return;

    const currentHandlerId = this.dataset.stickyClickHandlerId || '';
    const nextHandlerId = `${Date.now()}-${Math.random()}`;

    if (currentHandlerId) {
      stickyButton.dataset.stickyClickHandlerId = '';
    }

    stickyButton.onclick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.handleAddToCartClick();
    };

    stickyButton.dataset.stickyClickHandlerId = nextHandlerId;
  }

  #handleScroll = () => {
    this.#updateStickyVisibility();
  };

  #handleResize = () => {
    this.#updateStickyVisibility();
  };

  #updateStickyVisibility() {
    if (!this.#buyButtonsBlock) return;

    const buyButtonsRect = this.#buyButtonsBlock.getBoundingClientRect();
    const shouldShowSticky = buyButtonsRect.top <= this.#showOffset;

    let footerIsEnteringViewport = false;
    if (this.#footer) {
      const footerRect = this.#footer.getBoundingClientRect();
      footerIsEnteringViewport = footerRect.top <= window.innerHeight;
    }

    if (footerIsEnteringViewport) {
      this.#hiddenByBottom = true;
      this.#hideStickyBar();
      return;
    }

    this.#hiddenByBottom = false;

    if (shouldShowSticky) {
      this.#showStickyBar();
    } else {
      this.#hideStickyBar();
    }
  }

  handleAddToCartClick = async () => {
    const productForm = this.#getProductForm();
    const targetButton =
      productForm?.querySelector('[ref="addToCartButton"]') ??
      this.#targetAddToCartButton;

    if (!targetButton || targetButton.disabled) return;

    this.#targetAddToCartButton = targetButton;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton?.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton?.removeAttribute('data-added');
    }, 800);
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
      this.#buyButtonsBlock = productForm.closest('.buy-buttons-block');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#updateButtonText();

    requestAnimationFrame(() => {
      this.#bindStickyButtonClick();
      this.#updateStickyVisibility();
    });
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * @param {CustomEvent} _event
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #showStickyBar() {
    const { stickyBar } = this.refs;
    if (this.#isStuck) return;

    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;
    if (!this.#isStuck) return;

    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}